import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { MapPin, Wifi, WifiOff, Clock, Navigation, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { mapApi } from '@/api';
import { toast } from 'sonner';

const AvailabilityToggle = ({ user, onUpdate }) => {
  const { i18n } = useTranslation();
  const [isAvailable, setIsAvailable] = useState(user?.is_available_now || false);
  const [location, setLocation] = useState({
    lat: user?.latitude || null,
    lng: user?.longitude || null
  });
  const [loading, setLoading] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [availableUntil, setAvailableUntil] = useState('');

  useEffect(() => {
    setIsAvailable(user?.is_available_now || false);
    if (user?.latitude && user?.longitude) {
      setLocation({ lat: user.latitude, lng: user.longitude });
    }
  }, [user]);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error(i18n.language === 'fr' 
        ? 'Géolocalisation non supportée' 
        : 'Geolocation not supported');
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setGettingLocation(false);
        toast.success(i18n.language === 'fr' 
          ? 'Position mise à jour !' 
          : 'Location updated!');
      },
      (error) => {
        setGettingLocation(false);
        toast.error(i18n.language === 'fr' 
          ? 'Impossible d\'obtenir votre position' 
          : 'Unable to get your location');
      }
    );
  };

  const toggleAvailability = async () => {
    const newStatus = !isAvailable;
    
    if (newStatus && !location.lat) {
      toast.error(i18n.language === 'fr' 
        ? 'Veuillez d\'abord définir votre position' 
        : 'Please set your location first');
      return;
    }

    setLoading(true);
    try {
      const data = {
        is_available_now: newStatus,
        latitude: location.lat,
        longitude: location.lng,
        available_until: newStatus && availableUntil ? new Date(availableUntil).toISOString() : null
      };

      await mapApi.updateAvailability(data);
      setIsAvailable(newStatus);
      
      toast.success(newStatus 
        ? (i18n.language === 'fr' ? 'Vous êtes maintenant visible sur la carte !' : 'You are now visible on the map!')
        : (i18n.language === 'fr' ? 'Vous n\'êtes plus visible sur la carte' : 'You are no longer visible on the map'));
      
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating availability');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl p-6 border-2 transition-all ${
        isAvailable 
          ? 'bg-green-50 border-green-300' 
          : 'bg-white border-gray-200'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
            isAvailable ? 'bg-green-500' : 'bg-gray-300'
          }`}>
            {isAvailable ? (
              <Wifi className="w-6 h-6 text-white" />
            ) : (
              <WifiOff className="w-6 h-6 text-white" />
            )}
          </div>
          <div>
            <h3 className="font-bold text-lg">
              {i18n.language === 'fr' ? 'Disponibilité en direct' : 'Live Availability'}
            </h3>
            <p className={`text-sm ${isAvailable ? 'text-green-600' : 'text-gray-500'}`}>
              {isAvailable 
                ? (i18n.language === 'fr' ? 'Vous êtes visible sur la carte' : 'You are visible on the map')
                : (i18n.language === 'fr' ? 'Vous n\'êtes pas visible' : 'You are not visible')}
            </p>
          </div>
        </div>
        
        <Button
          onClick={toggleAvailability}
          disabled={loading}
          className={isAvailable 
            ? 'bg-red-500 hover:bg-red-600' 
            : 'bg-green-500 hover:bg-green-600'}
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : isAvailable ? (
            <>{i18n.language === 'fr' ? 'Se déconnecter' : 'Go offline'}</>
          ) : (
            <>{i18n.language === 'fr' ? 'Me rendre disponible' : 'Go available'}</>
          )}
        </Button>
      </div>

      {/* Location */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-primary" />
          <span className="font-medium">
            {i18n.language === 'fr' ? 'Ma position' : 'My location'}
          </span>
        </div>
        
        {location.lat ? (
          <div className="flex items-center gap-2 p-3 bg-white rounded-lg border">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-sm text-gray-600">
              {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={getCurrentLocation}
              disabled={gettingLocation}
              className="ml-auto"
            >
              {gettingLocation ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Navigation className="w-4 h-4" />
              )}
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            onClick={getCurrentLocation}
            disabled={gettingLocation}
            className="w-full"
          >
            {gettingLocation ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {i18n.language === 'fr' ? 'Localisation...' : 'Getting location...'}
              </>
            ) : (
              <>
                <Navigation className="w-4 h-4 mr-2" />
                {i18n.language === 'fr' ? 'Obtenir ma position' : 'Get my location'}
              </>
            )}
          </Button>
        )}

        {/* Available until */}
        {!isAvailable && (
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {i18n.language === 'fr' ? 'Disponible jusqu\'à (optionnel)' : 'Available until (optional)'}
            </label>
            <Input
              type="datetime-local"
              value={availableUntil}
              onChange={(e) => setAvailableUntil(e.target.value)}
              min={new Date().toISOString().slice(0, 16)}
            />
          </div>
        )}
      </div>

      {/* Info */}
      <p className="text-xs text-gray-500 mt-4">
        {i18n.language === 'fr' 
          ? 'En vous rendant disponible, les voyageurs pourront vous voir sur la carte et vous contacter plus facilement.'
          : 'By going available, travelers will be able to see you on the map and contact you more easily.'}
      </p>
    </motion.div>
  );
};

export default AvailabilityToggle;
